/* eslint-disable react/self-closing-comp */
import { useCallback, useEffect, useState } from 'react';

import Link from 'next/link';
import debounce from 'lodash.debounce';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import api from 'services/api';
import { IState } from 'store';
import { useMiniCart } from 'hooks';
import { ICartProps } from 'store/modules/cart';
import { objectIsEmpty } from 'utils/objectEmpty';
import { IConfigProps } from 'store/modules/config';
import { IBannerProps } from 'store/modules/showcase';
import { IUserProps, signOut } from 'store/modules/profile';

import BannerItem from 'components/common/Templates/BannerItem';
import { BasketWithItems } from 'components/common/Templates/BasketWithItems';

import Search from './Search';
import MiniCart from '../MiniCart';
import HeaderMobile from './HeaderMobile';
import { MemoizedRegion } from './Region';
import NavigationTree from './NavigationTree';
import NavigationSection from './NavigationSection';

import * as S from './styles';
import Timer from 'components/common/Timer';

interface HeaderProps {
  initialPages: boolean;
  showNavigationSection: boolean;
}

export default function Header({
  initialPages,
  showNavigationSection,
}: HeaderProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { dropMiniCart, dropped } = useMiniCart();
  const cart = useSelector<IState, ICartProps>(state => state.cart.cart);
  const user = useSelector<IState, IUserProps>(state => state.profile.user);
  const { config } = useSelector<IState, IConfigProps>(state => state.config);

  const [windowWidth, setWindowWidth] = useState('web');
  const [banners, setBanners] = useState<IBannerProps[]>([]);

  function resizeWindow() {
    if (typeof window !== 'undefined') {
      if (window.innerWidth > 580) {
        setWindowWidth('web');
      } else {
        setWindowWidth('mobile');
      }
    }
  }

  useEffect(() => {
    resizeWindow();

    window.addEventListener('resize', resizeWindow);

    return () => window.removeEventListener('resize', resizeWindow);
  }, []);

  const handleScroll = debounce(() => {
    if (typeof window === 'undefined') return;

    let headerWeb = document.getElementById('header-top');
    let headerMobile = document.getElementById('header-top-mobile');

    let timerWeb = document.getElementById('timer web');
    let timerHome = document.getElementById('timer home');
    let timerMobile = document.getElementById('timer mobile');

    let lastInnerHeight = window.innerHeight;
    let keyboardOpen = false;

    document.body.onscroll = ev => {
      const scrollY = (ev.composedPath()[1] as any).pageYOffset;
      const isDesktop = window.innerWidth > 1200;

      // Detecta abertura do teclado (redução súbita de altura visível)
      const currentHeight = window.innerHeight;
      keyboardOpen = lastInnerHeight - currentHeight > 150;
      lastInnerHeight = currentHeight;

      if (keyboardOpen) {
        // Quando teclado está aberto, evite mudanças de layout
        return;
      }

      const swap = document.getElementById('height-swap');
      if (!swap) return;

      if (isDesktop) {
        const headerWebHeight = headerWeb?.offsetHeight || 0;

        if (scrollY > headerWebHeight) {
          swap.style.height = `${headerWebHeight}px`;
          headerWeb?.classList.add('fixed');

          if (location.pathname === '/') {
            timerHome && (timerHome.style.display = 'none');
            timerWeb && (timerWeb.style.display = 'flex');
            timerMobile && (timerMobile.style.display = 'none');
          }
        } else {
          swap.style.height = '0px';
          headerWeb?.classList.remove('fixed');

          if (location.pathname === '/') {
            timerHome && (timerHome.style.display = 'flex');
            timerWeb && (timerWeb.style.display = 'none');
            timerMobile && (timerMobile.style.display = 'none');
          }
        }
      } else {
        const headerMobileHeight = headerMobile?.offsetHeight || 0;

        if (scrollY > headerMobileHeight) {
          swap.style.height = `${headerMobileHeight}px`;
          headerMobile?.classList.add('fixed');

          if (location.pathname === '/') {
            timerHome && (timerHome.style.display = 'none');
            timerWeb && (timerWeb.style.display = 'none');
            timerMobile && (timerMobile.style.display = 'flex');
          }
        } else {
          swap.style.height = '0px';
          headerMobile?.classList.remove('fixed');

          if (location.pathname === '/') {
            timerHome && (timerHome.style.display = 'flex');
            timerWeb && (timerWeb.style.display = 'none');
            timerMobile && (timerMobile.style.display = 'none');
          }
        }
      }
    };
  }, 100);

  useEffect(() => {
    if (!initialPages) {
      window.addEventListener('scroll', handleScroll);

      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [initialPages]);

  useEffect(() => {
    const getBannersTopo = async () => {
      try {
        const response = await api.get('banners/home/topo');

        setBanners(response.data);
      } catch (err) {}
    };

    getBannersTopo();
  }, []);

  const handleNavigateToLogin = useCallback(() => {
    if (objectIsEmpty(user)) {
      router.push('/identificacao');
    } else {
      router.push('/central-do-cliente/pedidos');
    }
  }, [router, user]);

  const handleSignOut = useCallback(() => {
    dispatch(signOut({}));
  }, [dispatch]);

  return (
    <>
      {dropped && windowWidth === 'web' && <MiniCart onClose={dropMiniCart} />}

      <S.Container className="header">
        <div className="header-top-mobile" id="header-top-mobile">
          <div className="banner-item-group mobile">
            {banners.length > 0 &&
              banners.map((banner, index) => (
                <BannerItem key={banner.id} banner={banner} index={index} />
              ))}
          </div>

          <HeaderMobile />

          <Timer containerClassName="mobile" />
        </div>

        <header className="header-background-top" id="header-top">
          <div className="banner-item-group web">
            {banners.length > 0 &&
              banners.map((banner, index) => (
                <BannerItem key={banner.id} banner={banner} index={index} />
              ))}
          </div>

          <div className="header-top">
            <Link href="/" passHref>
              <a className="logo">
                <img
                  loading="lazy"
                  src={
                    config.aparencia?.logo
                      ? config.aparencia.logo
                      : '/images/ultrafarma-sports-nutrition-logo.png'
                  }
                  alt="Ultrafarma Sports Nutrition"
                  className="header-logo"
                />
              </a>
            </Link>

            <MemoizedRegion />

            <Search />

            <div className="header-nav">
              <div className="header-nav-user">
                <img
                  src="/images/icons/user.svg"
                  alt="Usuário"
                  className="icon user-icon"
                />

                <div className="header-nav-user-info">
                  {!objectIsEmpty(user) ? (
                    <div onClick={handleNavigateToLogin}>
                      <p>Olá, {user.nome}</p>
                      <strong>Minha conta</strong>
                    </div>
                  ) : (
                    <div onClick={handleNavigateToLogin}>
                      <p>Olá, Visitante!</p>
                      <strong>Cadastre-se | Login</strong>
                    </div>
                  )}
                </div>
              </div>

              <div
                className="header-nav-shopping"
                onClick={() => {
                  if (!router.asPath.includes('/checkout')) dropMiniCart();
                }}
              >
                <BasketWithItems
                  color="white"
                  quantity={cart.totalItens}
                  style={{ position: 'relative' }}
                />
                <span>{cart.subtotalTexto || 'R$ 0,00'}</span>
              </div>
            </div>
          </div>

          <Timer containerClassName="web" />
        </header>

        <NavigationTree />

        {showNavigationSection && <NavigationSection />}
      </S.Container>
    </>
  );
}
